import { CurrencyCode } from '@99designs/i18n';
import {
    convertUSDToLocalisedAmountInCents,
    roundAmountInCentsToNearestNine,
} from '../helpers/currencyHelpers';
import { industryToSquarespaceMap } from './IndustryStep/industryToSquarespaceMap';
import JimdoLogo from './images/products/jimdo-logo-colour.svg';
import MagentoLogo from './images/products/magento-logo-colour.svg';
import ShopifyLogo from './images/products/shopify-logo-colour.svg';
import SquareLogo from './images/products/square-logo-colour.svg';
import SquarespaceLogo from './images/products/squarespace-logo-colour.svg';
import WeeblyLogo from './images/products/weebly-logo-colour.svg';
import WixLogo from './images/products/wix-logo-colour.svg';
import type { Money } from '../components/Price';
import type { DeliverableTypeValue } from './DeliverableTypeStep';
import type { ExchangeRate } from './Recommendation/LocalExchangeRateContext';
import type { CreateBriefFromProductCardMutationVariables } from './StartBrief/CreateBriefFromProductCard.generated';
import type { WorkingMethodValue } from './WorkingMethodStep';
import type { Data } from './config';

interface ProductFeature {
    title: string;
    description?: string;
}

interface AdditionalDetail {
    title: string;
    description?: string;
}

export interface Product {
    key: string;
    createKey: string;
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    logo?: any;
    description: string;
    features: ProductFeature[];
    additionalDetails?: AdditionalDetail[];
    pricing: Pricing;
    getLocalisedPricing: (exchangeRate?: ExchangeRate) => Pricing;
    whatYouGet: string;
    featureColor?: string;
    deliverableType: DeliverableTypeValue;
    workingMethod: WorkingMethodValue;
    getCategory: () => string;
    getCreateBriefVariables: (data: Data) => CreateBriefFromProductCardMutationVariables;
    isAvailableForLocale: (locale: string) => boolean;
}

interface Pricing {
    fromPrice: Money;
    isFixed?: boolean;
    priceDisclaimer?: string;
}

function getCategory(): string {
    return this.key.split(':')[1];
}

const availableInAllLocales = () => true;

function getPricingByExchangeRate(exchangeRate: ExchangeRate): Pricing {
    if (exchangeRate.currency === 'USD') {
        return this.pricing;
    }

    const localisedPrice = convertUSDToLocalisedAmountInCents(
        this.pricing.fromPrice.amountInCents,
        exchangeRate
    );

    const pricing = {
        ...this.pricing,
        fromPrice: {
            amountInCents: roundAmountInCentsToNearestNine(
                localisedPrice.amountInCents,
                localisedPrice.currency
            ),
            currency: localisedPrice.currency,
        },
    };

    return pricing;
}

export const products = (): Product[] => [
    {
        key: 'project:squarespace',
        createKey: 'squarespace-generic',
        name: '',
        logo: SquarespaceLogo,
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            priceDisclaimer: __('plus ongoing third-party hosting fees.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('A finished, live and fully functioning website.'),
        featureColor: '#000000',
        deliverableType: 'complete-website',
        workingMethod: 'project',
        getCategory,
        getCreateBriefVariables: (data: Data): CreateBriefFromProductCardMutationVariables => {
            return {
                slug: 'squarespace',
                fields: {
                    industry: [data.industry],
                    needs: 'NEW_SITE',
                },
            };
        },
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'project:squarespace-web-essential',
        createKey: 'squarespace-web-essential',
        name: '',
        logo: SquarespaceLogo,
        description: '',
        features: [],
        additionalDetails: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            isFixed: true,
            priceDisclaimer: __('plus ongoing third-party hosting fees.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('A finished, live and fully functioning website.'),
        featureColor: '#000000',
        deliverableType: 'complete-website',
        workingMethod: 'project',
        getCategory,
        getCreateBriefVariables: (data: Data): CreateBriefFromProductCardMutationVariables => {
            return {
                slug: 'squarespace-web-essential',
                fields: {
                    industry: data.industry
                        ? industryToSquarespaceMap.get(data.industry)
                        : undefined,
                },
            };
        },
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'project:squarespace-web-commerce',
        createKey: 'squarespace-web-commerce',
        name: '',
        logo: SquarespaceLogo,
        description: '',
        features: [],
        additionalDetails: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            isFixed: true,
            priceDisclaimer: __('plus ongoing third-party hosting fees.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('A finished, live and fully functioning website.'),
        featureColor: '#000000',
        deliverableType: 'complete-website',
        workingMethod: 'project',
        getCategory,
        getCreateBriefVariables: (data: Data): CreateBriefFromProductCardMutationVariables => {
            return {
                slug: 'squarespace-web-commerce',
                fields: {
                    industry: data.industry
                        ? industryToSquarespaceMap.get(data.industry)
                        : undefined,
                },
            };
        },
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'project:square',
        createKey: 'square-generic',
        name: '',
        logo: SquareLogo,
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            priceDisclaimer: __('plus ongoing third-party hosting fees.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('A finished, live and fully functioning website.'),
        featureColor: '#3E4348',
        deliverableType: 'complete-website',
        workingMethod: 'project',
        getCategory,
        getCreateBriefVariables: (data: Data): CreateBriefFromProductCardMutationVariables => {
            return {
                slug: 'square-online-custom',
                fields: {
                    industry: data.industry,
                    projectType: 'NEW_SITE',
                },
            };
        },
        isAvailableForLocale: (locale: string) => {
            const supportedRegions = ['US', 'CA', 'JP', 'AU', 'GB'];
            const foundPosition = supportedRegions.findIndex((region) =>
                locale.toUpperCase().includes(region)
            );
            return foundPosition >= 0;
        },
    },
    {
        key: 'project:wix',
        createKey: 'wix',
        name: '',
        logo: WixLogo,
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            priceDisclaimer: __('plus ongoing third-party hosting fees.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('A finished, live and fully functioning website.'),
        featureColor: '#FBBF72',
        deliverableType: 'complete-website',
        workingMethod: 'project',
        getCategory,
        getCreateBriefVariables: (data: Data): CreateBriefFromProductCardMutationVariables => {
            return {
                slug: 'web-builder',
                fields: {
                    industry: data.industry,
                    platform: 'WIX',
                },
            };
        },
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'project:shopify',
        createKey: 'shopify',
        name: '',
        logo: ShopifyLogo,
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            priceDisclaimer: __('plus ongoing third-party hosting fees.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('A finished, live and fully functioning website.'),
        featureColor: '#95C147',
        deliverableType: 'complete-website',
        workingMethod: 'project',
        getCategory,
        getCreateBriefVariables: (data: Data): CreateBriefFromProductCardMutationVariables => {
            return {
                slug: 'web-builder',
                fields: {
                    industry: data.industry,
                    platform: 'SHOPIFY',
                },
            };
        },
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'project:jimdo',
        createKey: 'jimdo',
        name: '',
        logo: JimdoLogo,
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            priceDisclaimer: __('plus ongoing third-party hosting fees.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('A finished, live and fully functioning website.'),
        featureColor: '#BED54B',
        deliverableType: 'complete-website',
        workingMethod: 'project',
        getCategory,
        getCreateBriefVariables: (data: Data): CreateBriefFromProductCardMutationVariables => {
            return {
                slug: 'web-builder',
                fields: {
                    industry: data.industry,
                    platform: 'JIMDO',
                },
            };
        },
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'project:magento',
        createKey: 'magento',
        name: '',
        logo: MagentoLogo,
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            priceDisclaimer: __('plus ongoing third-party hosting fees.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('A finished, live and fully functioning website.'),
        featureColor: '#F26322',
        deliverableType: 'complete-website',
        workingMethod: 'project',
        getCategory,
        getCreateBriefVariables: (data: Data): CreateBriefFromProductCardMutationVariables => {
            return {
                slug: 'web-builder',
                fields: {
                    industry: data.industry,
                    platform: 'MAGENTO',
                },
            };
        },
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'project:weebly',
        createKey: 'weebly',
        name: '',
        logo: WeeblyLogo,
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            priceDisclaimer: __('plus ongoing third-party hosting fees.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('A finished, live and fully functioning website.'),
        featureColor: '#1097D6',
        deliverableType: 'complete-website',
        workingMethod: 'project',
        getCategory,
        getCreateBriefVariables: (data: Data): CreateBriefFromProductCardMutationVariables => {
            return {
                slug: 'web-builder',
                fields: {
                    industry: data.industry,
                    platform: 'WEEBLY',
                },
            };
        },
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'project:web-design',
        createKey: 'web-page-design-project',
        name: '',
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            priceDisclaimer: __('estimated price range.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('Photoshop (PSD) or Sketch design files.'),
        deliverableType: 'website-design',
        workingMethod: 'project',
        featureColor: `#22C67B`,
        getCategory,
        getCreateBriefVariables: function (
            data: Data
        ): CreateBriefFromProductCardMutationVariables {
            return {
                slug: 'project',
                fields: {
                    industry: data.industry,
                    category: this.getCategory(),
                },
            };
        },
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'project:landing-page-design',
        createKey: 'landing-page-design-project',
        name: '',
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
            priceDisclaimer: __('estimated price range.'),
        },
        getLocalisedPricing: getPricingByExchangeRate,
        whatYouGet: __('Photoshop (PSD) or Sketch design files.'),
        deliverableType: 'website-design',
        workingMethod: 'project',
        getCategory,
        getCreateBriefVariables: function (
            data: Data
        ): CreateBriefFromProductCardMutationVariables {
            return {
                slug: 'project',
                fields: {
                    industry: data.industry,
                    category: this.getCategory(),
                },
            };
        },
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'contest:web-design',
        createKey: 'web-page-design-contest',
        name: '',
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
        },
        getLocalisedPricing: function (): Pricing {
            return this.pricing;
        },
        whatYouGet: __('Photoshop (PSD) or Sketch design files.'),
        deliverableType: 'website-design',
        workingMethod: 'contest',
        getCategory,
        getCreateBriefVariables: () => ({
            slug: '',
            fields: {},
        }),
        isAvailableForLocale: availableInAllLocales,
    },
    {
        key: 'contest:landing-page-design',
        createKey: 'landing-page-design-contest',
        name: '',
        description: '',
        features: [],
        pricing: {
            fromPrice: {
                amountInCents: 0,
                currency: CurrencyCode.USD,
            },
        },
        getLocalisedPricing: function (): Pricing {
            return this.pricing;
        },
        whatYouGet: __('Photoshop (PSD) or Sketch design files.'),
        deliverableType: 'website-design',
        workingMethod: 'contest',
        getCategory,
        getCreateBriefVariables: () => ({
            slug: '',
            fields: {},
        }),
        isAvailableForLocale: availableInAllLocales,
    },
];

export function mustFindProductByKey(products: Product[], key: string): Product {
    const product = products.find((p) => p.key === key);
    if (!product) {
        // eslint-disable-next-line no-throw-literal
        throw 'product not found';
    }
    return product;
}
